import { defer, finalize, Observable, of, shareReplay, tap } from 'rxjs';

/** A short-lived, record-budgeted cache. It contains metadata, never image bytes. */
export class ListingPageCache {
  private entries = new Map<string, { value: any; expires: number; size: number }>();
  private pending = new Map<string, Observable<any>>();

  constructor(private maxRecords = 160, private ttlMs = 120000, private now = () => Date.now()) {}

  get(key: string, fetch: () => Observable<any>): Observable<any> {
    return defer(() => {
      this.prune();
      const cached = this.entries.get(key);
      if (cached) {
        this.entries.delete(key);
        this.entries.set(key, cached);
        return of(cached.value);
      }
      let request = this.pending.get(key);
      if (!request) {
        request = defer(fetch).pipe(
          tap(value => {
            if (!Array.isArray(value?.records) || !Number.isFinite(value.total)) return;
            const size = Math.max(1, value.records.length);
            // An older API may ignore pagination. Do not retain oversized responses.
            if (size > this.maxRecords) return;
            this.entries.set(key, { value, size, expires: this.now() + this.ttlMs });
            this.prune();
          }),
          finalize(() => this.pending.delete(key)),
          shareReplay({ bufferSize: 1, refCount: true })
        );
        this.pending.set(key, request);
      }
      return request;
    });
  }

  private prune() {
    const now = this.now();
    let size = 0;
    for (const [key, entry] of this.entries) {
      if (entry.expires <= now) this.entries.delete(key);
      else size += entry.size;
    }
    for (const [key, entry] of this.entries) {
      if (size <= this.maxRecords) break;
      this.entries.delete(key);
      size -= entry.size;
    }
  }
}

/** Include all request fields; tabs, open-only filters and pages cannot share responses. */
export function listingPageKey(filter: object): string {
  const coordinates = new Set(['origin_Lat', 'origin_long', 'north', 'south', 'east', 'west']);
  return JSON.stringify(Object.keys(filter).sort().map(key => {
    const value = filter[key];
    return [key, coordinates.has(key) && value != null && value !== '' && Number.isFinite(Number(value))
      ? Number(Number(value).toFixed(6)) : value];
  }));
}
